// [PURPOSE]: Pure-function helpers that render character export formats.
// [WHY]: The HTML export was previously inline in App.jsx and concatenated
//        user-controlled strings into the document body without escaping —
//        a stored XSS vector when the export is shared with other party
//        members (the export's documented use case). Extracting to a util
//        makes the escape boundary testable and the export reusable.
// [INTEGRATION]: Wired in src/App.jsx generateHTMLExport call site (Phase 2
//        audit S-8, Batch 1.5).

const HTML_ESCAPE_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Escape characters that are dangerous in HTML body content. Use whenever
 * concatenating user input into an HTML template literal.
 *
 * @param {*} value
 * @returns {string}
 */
function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/[&<>"']/g, (ch) => HTML_ESCAPE_MAP[ch] || ch);
}

const e = escapeHtml;

/**
 * Generate a self-contained HTML character sheet for download.
 *
 * @param {object} data
 * @param {object} data.playerStats - includes name, level, ability scores
 * @param {Array} data.inventory - list of items
 * @param {Array} data.vaults - list of vault objects
 * @param {object} data.currencies - cp/sp/ep/gp/pp
 * @param {string} [data.primaryClass]
 * @returns {string} full HTML document
 */
function generateHTMLExport(data) {
  const { playerStats = {}, inventory = [], vaults = [], currencies = {}, primaryClass } = data || {};

  const itemListHtml = inventory.map((item) => {
    // Total weight / value computed by the caller previously via InventoryItem
    // prototype methods; keep simple here since the export is a snapshot.
    const totalWeight = (item.weight || 0).toFixed(1);
    const totalValue = (item.value || 0).toFixed(1);

    const containedHtml = item.containedItems && item.containedItems.length > 0
      ? `<div class="contained-items">
            <strong>Contains:</strong>
            <ul>
              ${item.containedItems.map((ci) => `<li>${e(ci.name)}</li>`).join('')}
            </ul>
          </div>`
      : '';

    const descriptionHtml = item.description
      ? `<div class="item-details">${e(item.description)}</div>`
      : '';

    return `<li class="item">
      <div class="item-name">${e(item.name)}</div>
      <div class="item-details">Weight: ${e(totalWeight)} lbs | Value: ${e(totalValue)} gp</div>
      ${descriptionHtml}
      ${containedHtml}
    </li>`;
  }).join('');

  const vaultHtml = vaults.length > 0
    ? `<div class="section">
        <div class="section-title">Vaults (${vaults.length} locations)</div>
        ${vaults.map((vault) => `
          <div style="margin-bottom: 20px;">
            <h4>${e(vault.name)} (${e(vault.location || 'Unknown Location')})</h4>
            <p>Cost: ${e(vault.cost || 0)} gp/month | Items: ${vault.items?.length || 0}</p>
            <ul class="item-list">
              ${vault.items?.map((it) => `<li class="item"><div class="item-name">${e(it.name)}</div></li>`).join('') || ''}
            </ul>
          </div>
        `).join('')}
      </div>`
    : '';

  const characterName = playerStats.name || 'Unnamed Character';

  return `<!DOCTYPE html>
<html>
<head>
  <title>D&D Character Sheet - ${e(characterName)}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 40px; background: #f5f5f5; }
    .character-sheet { background: white; padding: 30px; border-radius: 10px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
    .header { text-align: center; border-bottom: 3px solid #8B5CF6; padding-bottom: 20px; margin-bottom: 30px; }
    .section { margin-bottom: 25px; }
    .section-title { font-size: 18px; font-weight: bold; color: #8B5CF6; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; margin-bottom: 15px; }
    .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
    .stat-box { background: #f8f9fa; padding: 10px; border-radius: 5px; text-align: center; }
    .currency-list { display: flex; gap: 20px; flex-wrap: wrap; }
    .currency { background: #fef3c7; padding: 8px 12px; border-radius: 5px; font-weight: bold; }
    .item-list { list-style: none; padding: 0; }
    .item { background: #f3f4f6; margin: 8px 0; padding: 12px; border-radius: 5px; border-left: 4px solid #8B5CF6; }
    .item-name { font-weight: bold; color: #374151; }
    .item-details { font-size: 14px; color: #6b7280; margin-top: 5px; }
    .contained-items { margin-top: 8px; padding-left: 15px; }
    .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px; }
  </style>
</head>
<body>
  <div class="character-sheet">
    <div class="header">
      <h1>D&D Character Sheet</h1>
      <h2>${e(characterName)}</h2>
      <p>${e(primaryClass || 'No Class')} &bull; Level ${e(playerStats.level || 1)}</p>
    </div>

    <div class="section">
      <div class="section-title">Ability Scores</div>
      <div class="stats-grid">
        <div class="stat-box"><strong>STR</strong><br>${e(playerStats.strength || 10)}</div>
        <div class="stat-box"><strong>DEX</strong><br>${e(playerStats.dexterity || 10)}</div>
        <div class="stat-box"><strong>CON</strong><br>${e(playerStats.constitution || 10)}</div>
        <div class="stat-box"><strong>INT</strong><br>${e(playerStats.intelligence || 10)}</div>
        <div class="stat-box"><strong>WIS</strong><br>${e(playerStats.wisdom || 10)}</div>
        <div class="stat-box"><strong>CHA</strong><br>${e(playerStats.charisma || 10)}</div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Currencies</div>
      <div class="currency-list">
        <div class="currency">CP: ${e(currencies.cp || 0)}</div>
        <div class="currency">SP: ${e(currencies.sp || 0)}</div>
        <div class="currency">EP: ${e(currencies.ep || 0)}</div>
        <div class="currency">GP: ${e(currencies.gp || 0)}</div>
        <div class="currency">PP: ${e(currencies.pp || 0)}</div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Inventory (${inventory.length} items)</div>
      <ul class="item-list">
        ${itemListHtml}
      </ul>
    </div>

    ${vaultHtml}

    <div class="footer">
      <p>Generated on ${e(new Date().toLocaleDateString())} at ${e(new Date().toLocaleTimeString())}</p>
      <p>Created with Tabletop Inventory Manager</p>
    </div>
  </div>
</body>
</html>`;
}

export { escapeHtml, generateHTMLExport };
